import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SupervisorLayout from '../../components/SupervisorLayout';
import SupervisorGroupCodeField from '../../components/SupervisorGroupCodeField';
import SupervisorBankCodeField from '../../components/SupervisorBankCodeField';
import LotterySearchModal from '../../components/LotterySearchModal';
import ControlNumbersModal from '../../components/ControlNumbersModal';
import {
  createSupervisorNumberLimit,
  deleteSupervisorNumberLimit,
  listSupervisorNumberLimits,
  updateSupervisorNumberLimit,
} from '../../api/supervisor';
import { listProducts } from '../../api/products';
import { resolveLotteryByCode } from '../../utils/lotteryLookup';
import './SupervisorCentralPage.css';
import './SupervisorNumberLimitPage.css';

const SupervisorNumberLimitPage = () => {
  const [scopeType, setScopeType] = useState('Groupe'); // 'Groupe' | 'Banque'
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');

  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');
  const [lotteryLookupError, setLotteryLookupError] = useState('');
  const [showLotteryModal, setShowLotteryModal] = useState(false);

  const [rowGroups, setRowGroups] = useState(null); // null = pas encore cherche
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);

  const handleScopeTypeChange = (type) => {
    setScopeType(type);
    setScopeId('');
    setScopeName('');
    setRowGroups(null);
  };

  const handleLotteryBlur = async () => {
    const c = String(lottery).trim();
    if (c === '') { setLotteryLookupError(''); return; }
    const res = await resolveLotteryByCode(c);
    if (res && res.code !== '0') {
      setLottery(res.code);
      setLotteryName(res.name);
      setLotteryLookupError('');
    } else {
      setLotteryLookupError('Loterie introuvable');
    }
  };

  const scope = scopeType === 'Groupe' ? { group: scopeId } : { bank: scopeId };

  const loadRows = () => {
    setLoadingRows(true);
    setError('');
    Promise.all([
      listProducts(),
      listSupervisorNumberLimits({ tirage: lottery, ...scope }),
    ])
      .then(([products, limits]) => {
        const byProduct = {};
        limits.forEach((nl) => { (byProduct[nl.product] ||= []).push(nl); });
        setRowGroups(products.map((p) => ({
          id: p.id,
          product: p.name,
          numbers: (byProduct[p.id] || []).map((nl) => ({
            id: nl.id, number: nl.numbers, limit: nl.online_limit, localLimit: nl.local_limit ?? '',
          })),
        })));
      })
      .catch(() => setError('Impossible de charger les limites.'))
      .finally(() => setLoadingRows(false));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!scopeId || !scopeName || !lottery) return;
    loadRows();
  };

  const products = (rowGroups || []).map((g) => g.product);

  const updateLimit = (groupId, numberId, field, value) => {
    setRowGroups((prev) => prev.map((g) => (
      g.id === groupId
        ? { ...g, numbers: g.numbers.map((n) => (n.id === numberId ? { ...n, [field]: value } : n)) }
        : g
    )));
  };

  const persistLimit = async (numberId, field) => {
    const num = rowGroups.flatMap((g) => g.numbers).find((n) => n.id === numberId);
    if (!num) return;
    const key = field === 'limit' ? 'online_limit' : 'local_limit';
    const value = field === 'localLimit' ? (String(num.localLimit).trim() === '' ? null : num.localLimit) : num.limit;
    try {
      await updateSupervisorNumberLimit(numberId, { [key]: value });
    } catch (err) {
      setError('Sauvegarde de la limite impossible.');
    }
  };

  const handleDelete = async (groupId, numberId) => {
    try {
      await deleteSupervisorNumberLimit(numberId);
      setRowGroups((prev) => prev.map((g) => (
        g.id === groupId ? { ...g, numbers: g.numbers.filter((n) => n.id !== numberId) } : g
      )));
    } catch (err) {
      setError('Suppression impossible.');
    }
  };

  const handleSaveNew = async ({ product, number, onlineLimit, localLimit }) => {
    const group = rowGroups.find((g) => g.product === product);
    if (!group) return;
    setError('');
    if (group.numbers.some((n) => n.number === number)) {
      setError(`Une limite existe déjà pour le numéro « ${number} » sur cette loterie.`);
      return;
    }
    try {
      const created = await createSupervisorNumberLimit({
        tirage: Number(lottery), product: group.id, ...scope,
        numbers: number, online_limit: onlineLimit, local_limit: localLimit || null,
      });
      setRowGroups((prev) => prev.map((g) => (
        g.id === group.id
          ? { ...g, numbers: [...g.numbers, { id: created.id, number: created.numbers, limit: created.online_limit, localLimit: created.local_limit ?? '' }] }
          : g
      )));
    } catch (err) {
      setError('Création de la limite impossible.');
    }
  };


  return (
    <SupervisorLayout>
      <p className="svc-breadcrumb">
        Ruta : <Link to="/supervisor/dashboard" className="svc-link">Menu</Link>
        {' >> '}
        <Link to="/supervisor/controles" className="svc-link">Controles</Link>
        {' >> '}
        <span>Limites par numéro</span>
      </p>
      <h2 className="svnl-title">Limites par numéro</h2>

      <div className="svnl-card">
        <form onSubmit={handleSearch} className="svnl-search-form">
          <div className="svnl-radio-row">
            {['Groupe', 'Banque'].map((opt) => (
              <label key={opt} className="svnl-radio-label">
                <input
                  type="radio"
                  name="scopeType"
                  checked={scopeType === opt}
                  onChange={() => handleScopeTypeChange(opt)}
                />
                {opt}
              </label>
            ))}
          </div>

          <div className="svnl-row">
            <label className="svnl-label">{scopeType}</label>
            {scopeType === 'Groupe' ? (
              <SupervisorGroupCodeField
                value={scopeId}
                name={scopeName}
                onChange={(id) => { setScopeId(id); setScopeName(''); setRowGroups(null); }}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); setRowGroups(null); }}
                inputClass="svnl-input svnl-input-sm"
                iconClass="svnl-icon-btn"
                nameClass="svnl-scope-name"
                errClass="svnl-lookup-err"
              />
            ) : (
              <SupervisorBankCodeField
                value={scopeId}
                name={scopeName}
                onChange={(id) => { setScopeId(id); setScopeName(''); setRowGroups(null); }}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); setRowGroups(null); }}
                inputClass="svnl-input svnl-input-sm"
                iconClass="svnl-icon-btn"
                nameClass="svnl-scope-name"
                errClass="svnl-lookup-err"
              />
            )}
          </div>

          <div className="svnl-row">
            <label className="svnl-label">Loterie</label>
            <input
              className="svnl-input svnl-input-sm"
              type="text"
              inputMode="numeric"
              value={lottery}
              onChange={(e) => { setLottery(e.target.value); setLotteryLookupError(''); }}
              onBlur={handleLotteryBlur}
            />
            <span className="svnl-icon-btn" onClick={() => setShowLotteryModal(true)}>🔍</span>
            {lotteryLookupError
              ? <span className="svnl-lookup-err">{lotteryLookupError}</span>
              : lotteryName && <span className="svnl-scope-name">{lotteryName}</span>}
          </div>

          {showLotteryModal && (
            <LotterySearchModal
              onSelect={(id, name) => {
                setLottery(String(id));
                setLotteryName(name || '');
                setLotteryLookupError('');
              }}
              onClose={() => setShowLotteryModal(false)}
            />
          )}

          <button type="submit" className="svnl-search-btn" disabled={!scopeId || !scopeName || !lottery || loadingRows}>
            {loadingRows ? 'Chargement...' : 'Chercher'}
          </button>
        </form>
      </div>

      {rowGroups && (
        <div className="svnl-card svnl-results-card">
          {error && <p className="svnl-error">{error}</p>}
          <table className="svnl-table">
            <thead>
              <tr>
                <th>Produit</th>
                <th>Numero</th>
                <th>Limite Online</th>
                <th>Limite Local</th>
                <th>
                  <button type="button" className="svnl-new-btn" onClick={() => setShowNewModal(true)}>Nouveau</button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rowGroups.map((group) => (
                <React.Fragment key={group.id}>
                  {group.numbers.length === 0 && (
                    <tr className="svnl-group-bare">
                      <td className="svnl-product-bold">{group.product}</td>
                      <td colSpan={4} className="svnl-empty-cell">Aucune limite</td>
                    </tr>
                  )}
                  {group.numbers.map((n, i) => (
                    <tr key={n.id}>
                      <td className="svnl-product-bold">{i === 0 ? group.product : ''}</td>
                      <td>{n.number}</td>
                      <td>
                        <input
                          className="svnl-cell-input"
                          value={n.limit}
                          onChange={(e) => updateLimit(group.id, n.id, 'limit', e.target.value)}
                          onBlur={() => persistLimit(n.id, 'limit')}
                        />
                      </td>
                      <td>
                        <input
                          className="svnl-cell-input"
                          value={n.localLimit}
                          placeholder="optionnel"
                          onChange={(e) => updateLimit(group.id, n.id, 'localLimit', e.target.value)}
                          onBlur={() => persistLimit(n.id, 'localLimit')}
                        />
                      </td>
                      <td>
                        <span className="svnl-delete-link" onClick={() => handleDelete(group.id, n.id)}>*Effacer</span>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNewModal && (
        <ControlNumbersModal
          lotteryLabel={`${lottery} - ${lotteryName}`}
          products={products}
          onSave={handleSaveNew}
          onClose={() => setShowNewModal(false)}
        />
      )}
    </SupervisorLayout>
  );
};

export default SupervisorNumberLimitPage;
